// ABOUTME: OAuth button for TikTok Shop. Opens backend-routed popup, validates
// ABOUTME: postMessage origin + state, resolves with the token payload + shops[].

import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Shbutton from '../../../../theme/buttons/Shbutton';
import Text from '../../../../theme/text/Text';

/**
 * TikTokOAuthButton
 *
 * Props:
 * - oauthUrl: string. Backend start endpoint (e.g. "https://api.stockhub.nl/api/oauth/tiktok/start").
 * - backendOrigin: string. Origin used to validate incoming postMessage events
 *   (e.g. "https://api.stockhub.nl").
 * - region: string. TikTok partner-app region passed to the start endpoint ("global" | "us").
 * - onSuccess: ({ access_token, refresh_token, expiresAt, seller_name, region, shops }) => void
 * - onError: (message: string) => void
 * - isAuthenticated: boolean
 * - onDisconnect: () => void
 * - disabled: boolean
 */
const TikTokOAuthButton = ({
    oauthUrl,
    backendOrigin,
    region,
    onSuccess,
    onError,
    isAuthenticated = false,
    onDisconnect,
    disabled = false,
}) => {
    const { t } = useTranslation();
    const [oauthLoading, setOauthLoading] = useState(false);
    const popupRef = useRef(null);
    const cancelRef = useRef(null);

    useEffect(() => {
        return () => {
            if (cancelRef.current) {
                cancelRef.current();
            }
            if (popupRef.current && !popupRef.current.closed) {
                popupRef.current.close();
            }
        };
    }, []);

    const openPopup = (uri, expectedState) => new Promise((resolve, reject) => {
        const handle = window.open(uri, '_blank', 'width=600,height=700');
        popupRef.current = handle;

        if (!handle) {
            reject(new Error('popup_blocked'));
            return;
        }

        let settled = false;
        let onMessage;
        let closedInterval;

        const cleanup = () => {
            settled = true;
            window.removeEventListener('message', onMessage);
            clearInterval(closedInterval);
            if (handle && !handle.closed) {
                handle.close();
            }
        };

        onMessage = (e) => {
            if (settled) return;
            if (e.origin !== backendOrigin) return;
            const data = e.data;
            if (!data || data.type !== 'tiktok_oauth') return;
            if (data.state !== expectedState) {
                cleanup();
                reject(new Error('state_mismatch'));
                return;
            }
            if (data.success) {
                cleanup();
                resolve({
                    access_token: data.access_token,
                    refresh_token: data.refresh_token,
                    expiresAt: data.expiresAt,
                    seller_name: data.seller_name,
                    region: data.region,
                    shops: data.shops,
                });
            } else {
                cleanup();
                reject(new Error(data.error || 'oauth_failed'));
            }
        };

        closedInterval = setInterval(() => {
            if (settled) return;
            if (!handle || handle.closed) {
                cleanup();
                reject(new Error('window_closed'));
            }
        }, 500);

        cancelRef.current = () => {
            if (!settled) {
                cleanup();
                reject(new Error('unmounted'));
            }
        };

        window.addEventListener('message', onMessage);
    });

    const handleAuthClick = async () => {
        if (!oauthUrl || !backendOrigin) {
            if (onError) onError(t('oauth_url_not_configured'));
            return;
        }

        setOauthLoading(true);
        try {
            const state = (window.crypto && window.crypto.randomUUID)
                ? window.crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const separator = oauthUrl.includes('?') ? '&' : '?';
            const params = new URLSearchParams({
                state,
                ...(region ? { region } : {}),
            });
            const fullUrl = `${oauthUrl}${separator}${params.toString()}`;

            const result = await openPopup(fullUrl, state);

            if (onSuccess) onSuccess(result);
        } catch (error) {
            if (error.message === 'unmounted') {
                return;
            }
            if (onError) {
                if (error.message === 'window_closed') {
                    onError(t('oauth_cancelled'));
                } else if (error.message === 'popup_blocked') {
                    onError(t('oauth_error'));
                } else {
                    onError(error.message || t('oauth_error'));
                }
            }
        } finally {
            setOauthLoading(false);
            popupRef.current = null;
            cancelRef.current = null;
        }
    };

    const handleDisconnect = () => {
        if (onDisconnect) onDisconnect();
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {!isAuthenticated ? (
                    <Shbutton
                        variant="outlined"
                        onClick={handleAuthClick}
                        isLoading={oauthLoading}
                        disabled={oauthLoading || disabled || !oauthUrl}
                        fullWidth
                        startIcon={oauthLoading ? <CircularProgress size={16} /> : null}
                    >
                        {oauthLoading ? t('authenticating') : t('authenticate_with_tiktok')}
                    </Shbutton>
                ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box sx={{
                            flex: 1,
                            p: 1.5,
                            backgroundColor: '#e8f5e8',
                            borderRadius: 1,
                            border: '1px solid #4caf50',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}>
                            <Text variant="body2" color="success.main" sx={{ flex: 1 }}>
                                ✓ {t('tiktok_authentication_successful')}
                            </Text>
                        </Box>
                        <Shbutton
                            variant="outlined"
                            size="small"
                            onClick={handleDisconnect}
                            color="error"
                        >
                            {t('disconnect')}
                        </Shbutton>
                    </Box>
                )}

                {oauthLoading && (
                    <Box sx={{
                        p: 1.5,
                        backgroundColor: '#fff3cd',
                        borderRadius: 1,
                        border: '1px solid #ffc107',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}>
                        <CircularProgress size={16} />
                        <Text variant="body2" color="warning.dark">
                            {t('waiting_for_tiktok_authentication')}
                        </Text>
                    </Box>
                )}

                {!isAuthenticated && (
                    <Box sx={{
                        p: 1.5,
                        backgroundColor: '#f8f9fa',
                        borderRadius: 1,
                        border: '1px solid #e9ecef',
                    }}>
                        <Text variant="body2" color="textSecondary">
                            {t('tiktok_oauth_instructions')}
                        </Text>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default TikTokOAuthButton;
